import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { type ReactNode } from 'react'

import type { PosCheckoutResponse } from '@aeris/contracts'
import {
  MONEY_SCALE,
  parseHumanAmount,
  ZERO_MONEY,
  type Decimal,
} from '@aeris/money'
import {
  receiptFor,
  renderReceiptDocumentHtml,
  renderText,
  type PaperWidth,
} from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Select,
} from '@aeris/ui'

import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import { printedCustomerOf } from '../lib/printed-customer.ts'
import { voucherOnReceipt } from '../lib/receipt-voucher.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

/**
 * The receipt, on screen and on paper.
 *
 * Master Specification section 24 asks for an Aeris Print Engine and says, in
 * one line, the thing that shapes it: **do not depend on `window.print()` as
 * the desktop solution.** So the receipt is built by `@aeris/printing` as
 * DATA, and this is one renderer of it — the browser's.
 *
 * A browser has exactly one way to reach a printer, and it is
 * `window.print()`. That is a limitation of the browser and not of the
 * architecture: the same `PrintDocument` already encodes to ESC/POS bytes,
 * which is what the desktop shell and the Edge will write straight to the
 * till's thermal printer, with no dialog, no margins and a cut at the end. The
 * document does not change when that lands. The transport does.
 *
 * ## Why a monospaced preview rather than a designed one
 *
 * What is on screen is `renderText` at the chosen paper width — character for
 * character what the thermal printer will put on the roll, produced by the
 * same layout function the ESC/POS encoder uses. A prettier preview would be a
 * second design that disagrees with the paper, and the disagreement would only
 * ever be discovered by a customer holding the receipt.
 */

const amount = (wire: string): ReturnType<typeof parseHumanAmount> =>
  parseHumanAmount(wire.replace('.', ','), MONEY_SCALE)

const money = (wire: string) => amount(wire) ?? ZERO_MONEY

/** `2,5` rather than `2.500000`, which is what a person reads on paper. */
const quantity = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

/**
 * O recibo de uma venda feita SEM internet (F197).
 *
 * Ainda não tem número — a numeração é da nuvem, e a venda ganha o dela quando
 * subir. O papel sai com a referência local do Edge no lugar do documento, e
 * por isso sem código de barras (que é o número). Antes a venda offline saía
 * sem papel nenhum, e o cliente ia embora sem comprovante.
 */
export type ProvisionalReceipt = {
  readonly reference: string
  readonly branchName: string
  readonly issuedAt: Date
  readonly operatorName: string | null
  readonly lines: readonly {
    readonly description: string
    /** Como vem da fiação: `2` ou `1.500000`. */
    readonly quantity: string
    readonly unit: string
    readonly unitPrice: Decimal
    readonly lineTotal: Decimal
  }[]
  readonly subtotal: Decimal
  readonly discount: Decimal
  readonly total: Decimal
  readonly payments: readonly {
    readonly method: string
    readonly amount: Decimal
  }[]
  readonly changeDue: Decimal
}

export type ReceiptDialogProps = {
  sale: PosCheckoutResponse | ProvisionalReceipt
  terminal: string | null
  paper: PaperWidth
  onPaperChange: (paper: PaperWidth) => void
  onClose: () => void
}

export const ReceiptDialog = ({
  sale,
  terminal,
  paper,
  onPaperChange,
  onClose,
}: ReceiptDialogProps): ReactNode => {
  // O cabeçalho da loja e o modelo dela (F149). Nenhuma requisição a mais: as
  // duas consultas já estão em cache quando o diálogo abre.
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const { allows } = useAuth()

  /* O vale do cliente no pé da nota (15/10): o saldo já com esta venda. */
  const partyId = 'reference' in sale ? null : sale.document.customerPartyId
  const credit = useQuery({
    queryKey: ['customer-credit', partyId, 'receipt'],
    queryFn: ({ signal }) =>
      fetchCustomerCredit(partyId ?? '', signal, { page: 1, pageSize: 20 }),
    enabled: partyId !== null && allows('exchange.read'),
  })

  const document =
    'reference' in sale
      ? receiptFor({
          template,
          company,
          branchName: sale.branchName,
          number: null,
          issuedAt: sale.issuedAt,
          customerName: 'Consumidor Final',
          operatorName: sale.operatorName,
          terminal,
          lines: sale.lines.map((line) => ({
            description: line.description,
            quantity: quantity(line.quantity),
            unit: line.unit,
            unitPrice: line.unitPrice,
            lineTotal: line.lineTotal,
          })),
          subtotal: sale.subtotal,
          discount: sale.discount,
          total: sale.total,
          payments: sale.payments,
          changeDue: sale.changeDue,
          documentId: `Venda offline ${sale.reference} — nº quando subir`,
        })
      : receiptFor({
          template,
          company,
          branchName: sale.document.branchName,
          number: sale.document.number,
          issuedAt: new Date(sale.document.issuedAt),
          customerName: sale.document.customerName,
          customer: printedCustomerOf(sale.document),
          operatorName: sale.document.salespersonName,
          terminal,
          lines: sale.document.lines.map((line) => ({
            description: line.description,
            quantity: quantity(line.quantity),
            unit: line.unit,
            unitPrice: money(line.unitPrice),
            lineTotal: money(line.lineTotal),
            code: line.sku,
            location: line.location,
            discount: money(line.discountAmount),
            priceOverridden: line.priceBasis === 'manual',
          })),
          subtotal: money(sale.document.subtotalAmount),
          discount: money(sale.document.discountAmount),
          total: money(sale.document.totalAmount),
          payments: sale.payments.map((payment) => ({
            method: payment.methodName,
            amount: money(payment.amount),
          })),
          changeDue: money(sale.settlement.changeDue),
          documentId: sale.document.id,
          voucher: voucherOnReceipt(credit.data),
        })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent title={document.title} description="Confira e imprima.">
        <div className="flex flex-col gap-3">
          <Field label="Papel" className="w-44">
            {(field) => (
              <Select
                {...field}
                value={String(paper)}
                onChange={(event) => {
                  onPaperChange(event.target.value === '48' ? 48 : 32)
                }}
              >
                <option value="32">58 mm</option>
                <option value="48">80 mm</option>
              </Select>
            )}
          </Field>

          {/*
            `aeris-receipt` is what the print stylesheet keeps; everything else
            on the page is hidden while printing. Without that, printing a
            receipt prints the sidebar, the topbar and the cart behind it.
          */}
          <pre
            id="aeris-receipt"
            aria-label="Recibo"
            className="overflow-x-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-3 font-(--font-mono) text-(length:--text-xs) leading-tight text-(--text-primary)"
          >
            {renderText(document, paper)}
          </pre>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              /*
               * Documento próprio, na largura da bobina e na altura do texto
               * (`renderReceiptDocumentHtml`, F140: negrito, TOTAL grande e o
               * código de barras de verdade). Se o quadro não abrir, cai no
               * jeito antigo, que imprime a página com o resto escondido.
               */
              printHtmlDocument(
                renderReceiptDocumentHtml(document, paper)
              ).catch(() => {
                window.print()
              })
            }}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
